import { For, type JSX } from 'solid-js'

type ParaglideMFunction<TInputs, TOptions> = (
  inputs: TInputs,
  options?: TOptions,
) => string

// Extract parameter names from paraglide function and map them to JSX.Element
type ExtractInputKeys<T> =
  T extends ParaglideMFunction<infer TInputs, any>
    ? TInputs extends Record<string, any>
      ? { [K in keyof TInputs]: JSX.Element }
      : Record<string, JSX.Element>
    : Record<string, JSX.Element>

type MessageProps<TMessage extends ParaglideMFunction<any, any>> = {
  message: TMessage
  inputs: ExtractInputKeys<TMessage>
}

export function Message<TMessage extends ParaglideMFunction<any, any>>(
  props: MessageProps<TMessage>,
) {
  const string = props.message(
    Object.fromEntries(
      Object.keys(props.inputs ?? {}).map((key) => [key, `{${key}}`]),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any,
  )
  const args = props.inputs

  if (!args) return string

  const parts: JSX.Element[] = []
  let lastIndex = 0

  const pattern = /{([^}]+)}/g
  let match: RegExpExecArray | null

  while ((match = pattern.exec(string)) !== null) {
    const [fullMatch, key] = match
    const value = args[key.trim()]

    if (match.index > lastIndex) {
      parts.push(string.slice(lastIndex, match.index))
    }

    if (value !== undefined) {
      parts.push(value)
    } else {
      parts.push(fullMatch)
    }

    lastIndex = match.index + fullMatch.length
  }

  if (lastIndex < string.length) {
    parts.push(string.slice(lastIndex))
  }

  if (parts.length === 0) return string

  return <For each={parts}>{(part) => part}</For>
}
